/**
 * The inspection functions and the photo bucket, as the console calls them.
 *
 * The functions are typed by the generated client. Each returns jsonb, which the generator types as
 * `Json`, so the payload shapes stay written by hand in `src/lib/inspections.ts` as the documented
 * contract, and each result is cast to its shape here.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import {
    INSPECTION_PHOTO_BUCKET,
    inspectionPhotoPath,
    type Checklist,
    type ContractInspections,
    type InspectionBoard,
    type InspectionKind,
    type RecordInspectionResult,
    type SubmittedLine,
    type SubmittedPhoto,
} from '@/lib/inspections';

export const inspectionKeys = {
    all: ['inspections'] as const,
    board: (period: string | null) => ['inspections', 'board', period ?? 'current'] as const,
    contract: (contractId: number) => ['inspections', 'contract', contractId] as const,
    checklist: ['inspections', 'checklist'] as const,
    carContract: (vehicleId: number) => ['inspections', 'car-contract', vehicleId] as const,
    car: (vehicleId: number) => ['inspections', 'car', vehicleId] as const,
};

/** The month board: one call, for the round whose span holds today unless `period` names one. */
export function useInspectionBoard(period: string | null, enabled = true) {
    return useQuery({
        queryKey: inspectionKeys.board(period),
        queryFn: async (): Promise<InspectionBoard> => {
            const { data, error } = await supabase.rpc('get_inspection_board', { p_period: period });
            if (error) throw error;
            return data as unknown as InspectionBoard;
        },
        enabled,
    });
}

/** One contract's inspections — for an admin, the car's view: every visit on the vehicle. */
export async function fetchContractInspections(contractId: number): Promise<ContractInspections | null> {
    const { data, error } = await supabase.rpc('get_contract_inspections', { p_contract_id: contractId });
    if (error) throw error;
    return (data ?? null) as unknown as ContractInspections | null;
}

export function useContractInspections(contractId: number | null, enabled = true) {
    return useQuery({
        queryKey: inspectionKeys.contract(contractId ?? 0),
        queryFn: () => fetchContractInspections(contractId!),
        enabled: enabled && !!contractId,
    });
}

/** The checklist alone, for a car that has never had a contract to read it through. */
export function useInspectionChecklist(enabled: boolean) {
    return useQuery({
        queryKey: inspectionKeys.checklist,
        queryFn: async (): Promise<Checklist> => {
            const { data, error } = await supabase.rpc('inspection_checklist');
            if (error) throw error;
            return data as unknown as Checklist;
        },
        enabled,
        staleTime: 60 * 60 * 1000,
    });
}

/**
 * The car's most recent contract, whatever its status — only a key to read the car's view through
 * get_contract_inspections (an admin gets every visit on the vehicle whichever contract is named).
 * It says nothing about which contract a visit is recorded on: record_inspection derives that.
 */
export function useLatestContractOfCar(vehicleId: number, enabled: boolean) {
    return useQuery({
        queryKey: inspectionKeys.carContract(vehicleId),
        queryFn: async (): Promise<number | null> => {
            const { data, error } = await supabase
                .from('contracts')
                .select('id')
                .eq('vehicle_id', vehicleId)
                .order('id', { ascending: false })
                .limit(1);
            if (error) throw error;
            return data?.[0]?.id ?? null;
        },
        enabled: enabled && vehicleId > 0,
    });
}

export interface CarBasics {
    id: number;
    make: string | null;
    model: string | null;
    plate: string | null;
}

/** Plate, make and model of a car, for the rare car with no contract to read a header through. */
export function useCarBasics(vehicleId: number, enabled: boolean) {
    return useQuery({
        queryKey: inspectionKeys.car(vehicleId),
        queryFn: async (): Promise<CarBasics | null> => {
            const { data, error } = await supabase
                .from('vehicles')
                .select('id, make, model, vehicle_registrations!vehicles_registration_id_fkey(number)')
                .eq('id', vehicleId)
                .maybeSingle();
            if (error) throw error;
            if (!data) return null;
            const reg = Array.isArray(data.vehicle_registrations) ? data.vehicle_registrations[0] : data.vehicle_registrations;
            return { id: data.id, make: data.make ?? null, model: data.model ?? null, plate: reg?.number ?? null };
        },
        enabled: enabled && vehicleId > 0,
    });
}

export interface RecordInspectionArgs {
    vehicleId: number;
    kind: InspectionKind;
    clientRef: string;
    odometerKm: number | null;
    lastServiceKm: number | null;
    remarks: string | null;
    lines: SubmittedLine[];
    photos: SubmittedPhoto[];
}

/**
 * Records the visit. Throws the PostgrestError on a refusal — its message starts with the stable
 * code and `details` carries INSPECTION_ALREADY_RECORDED's id — for `inspectionRefusal` to word.
 */
export async function recordInspection(args: RecordInspectionArgs): Promise<RecordInspectionResult> {
    const { data, error } = await supabase.rpc('record_inspection', {
        p_vehicle_id: args.vehicleId,
        p_kind: args.kind,
        p_client_ref: args.clientRef.toLowerCase(),
        p_odometer_km: args.odometerKm,
        p_last_service_km: args.lastServiceKm,
        p_remarks: args.remarks,
        p_lines: args.lines as unknown as Json,
        p_photos: args.photos,
    });
    if (error) throw error;
    return data as unknown as RecordInspectionResult;
}

/**
 * Uploads one photo to `inspections/<vehicle_id>/<client_ref>/<name>.jpg` with upsert, so a retry
 * overwrites, and returns the public-style URL the house stores (signed when read, through
 * SignedImage: the bucket is private).
 */
export async function uploadInspectionPhoto(vehicleId: number, clientRef: string, name: string, photo: Blob): Promise<string> {
    const path = inspectionPhotoPath(vehicleId, clientRef, name);
    const bucket = supabase.storage.from(INSPECTION_PHOTO_BUCKET);
    const { error } = await bucket.upload(path, photo, { upsert: true, contentType: 'image/jpeg', cacheControl: '3600' });
    if (error) throw error;
    return bucket.getPublicUrl(path).data.publicUrl;
}
